import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface NavigationTarget {
  id: string;
  name: string;
}

function getTargetUrl(target: NavigationTarget): string {
  if (target.id.startsWith('bio:')) {
    return `/bio/${target.id.slice(4)}`;
  }
  return `/poem/${target.id}`;
}

interface SectionCallbacks {
  onPrevSection: (() => void) | null;
  onNextSection: (() => void) | null;
}

/**
 * Hook that adds keyboard arrow key navigation and touch swipe gestures
 * for navigating between poems/biography pages.
 * When section callbacks are provided they take priority; poem navigation
 * is only triggered when already at the section boundary.
 */
export function usePageNavigation(
  prev: NavigationTarget | null,
  next: NavigationTarget | null,
  sections?: SectionCallbacks
) {
  const navigate = useNavigate();
  const touchStartRef = useRef<{ x: number; y: number } | null>(null);

  // Keep callbacks in a ref so the effect doesn't need to re-register
  const sectionsRef = useRef(sections);
  sectionsRef.current = sections;

  // Keyboard navigation (left/right arrow keys)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Don't navigate if user is typing in an input/textarea
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === 'INPUT' || tag === 'TEXTAREA' || tag === 'SELECT') return;

      if (e.key === 'ArrowLeft') {
        const prevSection = sectionsRef.current?.onPrevSection;
        if (prevSection) {
          e.preventDefault();
          prevSection();
        } else if (prev) {
          e.preventDefault();
          navigate(getTargetUrl(prev));
        }
      } else if (e.key === 'ArrowRight') {
        const nextSection = sectionsRef.current?.onNextSection;
        if (nextSection) {
          e.preventDefault();
          nextSection();
        } else if (next) {
          e.preventDefault();
          navigate(getTargetUrl(next));
        }
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [prev, next, navigate]);

  // Touch swipe handlers
  const onTouchStart = useCallback((e: React.TouchEvent) => {
    const touch = e.touches[0];
    touchStartRef.current = { x: touch.clientX, y: touch.clientY };
  }, []);

  const onTouchEnd = useCallback((e: React.TouchEvent) => {
    if (!touchStartRef.current) return;

    const touch = e.changedTouches[0];
    const deltaX = touch.clientX - touchStartRef.current.x;
    const deltaY = touch.clientY - touchStartRef.current.y;
    touchStartRef.current = null;

    // Only trigger if horizontal swipe is dominant and exceeds threshold
    const minSwipeDistance = 50;
    if (Math.abs(deltaX) < minSwipeDistance || Math.abs(deltaY) > Math.abs(deltaX)) {
      return;
    }

    if (deltaX > 0) {
      // Swipe right → previous section first, then previous poem
      const prevSection = sectionsRef.current?.onPrevSection;
      if (prevSection) {
        prevSection();
      } else if (prev) {
        navigate(getTargetUrl(prev));
      }
    } else if (deltaX < 0) {
      // Swipe left → next section first, then next poem
      const nextSection = sectionsRef.current?.onNextSection;
      if (nextSection) {
        nextSection();
      } else if (next) {
        navigate(getTargetUrl(next));
      }
    }
  }, [prev, next, navigate]);

  return { onTouchStart, onTouchEnd };
}

